'use client';

import { FC, useState } from 'react';
import { Dialog, DialogContent, Box, DialogActions, Button } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { TheoryActivityGridCore, TheoryActivityRowType } from '@/components/tables/TheoryActivityGridCore';
import { TheoryActivitySearchPanel } from '@/components/tables/search/TheoryActivitySearchPanel';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';

interface TheoryActivitySelectionDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Title of the dialog */
  title?: string;
  /** Callback when an activity is selected (single selection) */
  onActivitySelect?: (activity: TheoryActivityRowType) => void;
  /** Callback when the dialog is closed */
  onClose: () => void;
  /** Whether to allow multiple selection (default: true for data package use case) */
  allowMultiple?: boolean;
  /** Callback when multiple activities are selected (only used if allowMultiple is true) */
  onActivitiesSelect?: (activities: TheoryActivityRowType[]) => void;
}

const OPTION_KEYS: OptionKey[] = ['synthesisTechniques', 'status'];

export const TheoryActivitySelectionDialog: FC<TheoryActivitySelectionDialogProps> = ({
  open,
  title = 'Select Theory Activities',
  onActivitySelect,
  onClose,
  allowMultiple = true,
  onActivitiesSelect,
}) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ ids: new Set(), type: 'include' });
  const [selectedActivities, setSelectedActivities] = useState<TheoryActivityRowType[]>([]);

  // Reset state when dialog opens/closes
  const handleClose = () => {
    setSelectionModel({ ids: new Set(), type: 'include' });
    setSelectedActivities([]);
    onClose();
  };

  const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
    setSelectionModel(newSelectionModel);
  };

  const handleActivitySelect = (activity: TheoryActivityRowType) => {
    if (allowMultiple) {
      // In multiple mode, we don't auto-select, just update the selection model
      // The user will click "Use Selection" to confirm
    } else {
      // In single mode, auto-select and close
      onActivitySelect?.(activity);
      handleClose();
    }
  };

  const handleActivitiesSelect = (activities: TheoryActivityRowType[]) => {
    // Store the selected activities for use when "Use Selection" is clicked
    setSelectedActivities(activities);
  };

  const handleUseSelection = () => {
    if (allowMultiple) {
      // Multiple selection mode - use the current selection
      if (selectionModel.ids.size > 0 && selectedActivities.length > 0) {
        onActivitiesSelect?.(selectedActivities);
        handleClose();
      }
    }
    // Single selection is handled by handleActivitySelect
  };

  const canUseSelection = allowMultiple
    ? selectionModel.ids.size >= 1 // Multiple selection: allow if at least 1 activity selected
    : false; // Single selection: no need for button since it auto-selects

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <TheoryActivitySearchPanel title={title} isSelectionMode={true} />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <TheoryActivityGridCore
            showLinks={false}
            enableSelection={true}
            selectionModel={selectionModel}
            onSelectionChange={handleSelectionChange}
            checkboxSelection={allowMultiple}
            disableColumnMenu={true}
            showToolbar={false}
            density="compact"
            onActivitySelect={handleActivitySelect}
            onActivitiesSelect={handleActivitiesSelect}
            optionsData={options}
            optionsLoading={optionsLoading}
          />
        </Box>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleUseSelection} variant="contained" disabled={!canUseSelection}>
            Use Selection
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
